import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  campaignsApi,
  type Campaign,
  type CreateCampaignRequest,
  type UpdateCampaignRequest,
  type CampaignListParams,
  type Asset,
  type CreateAssetRequest,
  type Comment,
  type CreateCommentRequest,
  type UpdateCommentRequest,
  type BloomScoreResponse,
  type WaitlistSubscription,
  type CreateWaitlistSubscriptionRequest,
  type WaitlistCountResponse,
  type PricingFeedback,
  type CreatePricingFeedbackRequest,
  type PricingFeedbackAnalytics,
  type Response,
  type CreateResponseRequest,
} from "@/lib/campaignsApi";
import {
  authApi,
  type LoginRequest,
  type RegisterRequest,
} from "@/lib/authApi";
import {
  userApi,
  type UserProfile,
  type UpdateProfileRequest,
  type UserSettings,
  type UpdateSettingsRequest,
  type ChangePasswordRequest,
  type Notification,
  type UserCampaign,
  type UserActivity,
  type UserContribution,
} from "@/lib/userApi";
import {
  activityApi,
  type Activity,
  type ActivityFilters,
  type ActivityStats,
} from "@/lib/activityApi";
import {
  analyticsApi,
  type DashboardOverview,
  type UserAnalytics,
  type CampaignAnalytics,
  type EngagementAnalytics,
  type ConversionFunnel,
} from "@/lib/analyticsApi";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

// Query keys
export const queryKeys = {
  campaigns: ["campaigns"] as const,
  campaign: (id: string) => ["campaign", id] as const,
  myCampaigns: ["my-campaigns"] as const,
  user: ["user"] as const,
  userProfile: ["user-profile"] as const,
  userSettings: ["user-settings"] as const,
  userNotifications: ["user-notifications"] as const,
  userActivity: ["user-activity"] as const,
  userContributions: ["user-contributions"] as const,
  activities: ["activities"] as const,
  adminActivities: ["admin-activities"] as const,
  recentActivities: ["recent-activities"] as const,
  activityStats: ["activity-stats"] as const,
  analytics: ["analytics"] as const,
  dashboardOverview: ["analytics", "dashboard-overview"] as const,
  userAnalytics: ["analytics", "users"] as const,
  campaignAnalytics: ["analytics", "campaigns"] as const,
  engagementAnalytics: ["analytics", "engagement"] as const,
  conversionFunnel: ["analytics", "conversion-funnel"] as const,
};

// Campaign hooks
export const useCampaigns = (params?: { isPrivate?: boolean }) => {
  return useQuery({
    queryKey: [...queryKeys.campaigns, params],
    queryFn: () => campaignsApi.getCampaigns(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCampaign = (id: string) => {
  return useQuery({
    queryKey: queryKeys.campaign(id),
    queryFn: () => campaignsApi.getCampaign(id),
    enabled: !!id,
  });
};

export const useMyCampaigns = () => {
  return useQuery({
    queryKey: queryKeys.myCampaigns,
    queryFn: () => campaignsApi.getMyCampaigns(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCampaignRequest) =>
      campaignsApi.createCampaign(data),
    onSuccess: (newCampaign) => {
      // Invalidate and refetch campaigns lists
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns });
      queryClient.invalidateQueries({ queryKey: queryKeys.myCampaigns });

      // Add the new campaign to the cache
      queryClient.setQueryData(queryKeys.campaign(newCampaign.id), newCampaign);

      toast({
        title: "Campaign created successfully! 🎉",
        description: "Your campaign is now live and ready to collect feedback.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to create campaign",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignRequest }) =>
      campaignsApi.updateCampaign(id, data),
    onSuccess: (updatedCampaign) => {
      // Update the campaign in cache
      queryClient.setQueryData(
        queryKeys.campaign(updatedCampaign.id),
        updatedCampaign
      );

      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns });
      queryClient.invalidateQueries({ queryKey: queryKeys.myCampaigns });

      toast({
        title: "Campaign updated successfully! ✨",
        description: "Your changes have been saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to update campaign",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => campaignsApi.deleteCampaign(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.campaign(deletedId) });

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns });
      queryClient.invalidateQueries({ queryKey: queryKeys.myCampaigns });

      toast({
        title: "Campaign deleted successfully",
        description: "The campaign has been permanently removed.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to delete campaign",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    },
  });
};

export const useCampaignAssets = (campaignId: string) => {
  return useQuery({
    queryKey: ["campaign-assets", campaignId],
    queryFn: () => campaignsApi.getCampaignAssets(campaignId),
    enabled: !!campaignId,
  });
};

// Comment hooks
export const useCampaignComments = (campaignId: string) => {
  return useQuery({
    queryKey: ["campaign-comments", campaignId],
    queryFn: () => campaignsApi.getCampaignComments(campaignId),
    enabled: !!campaignId,
  });
};

export const useCreateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      data,
    }: {
      campaignId: string;
      data: CreateCommentRequest;
    }) => campaignsApi.createComment(campaignId, data),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-comments", campaignId],
      });
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
    },
  });
};

export const useUpdateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commentId,
      data,
    }: {
      commentId: string;
      data: UpdateCommentRequest;
    }) => campaignsApi.updateComment(commentId, data),
    onSuccess: (_, { commentId }) => {
      queryClient.invalidateQueries({ queryKey: ["campaign-comments"] });
    },
  });
};

export const useDeleteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) => campaignsApi.deleteComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-comments"] });
    },
  });
};

// Bloom Score hooks
export const useToggleBloomScore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) => campaignsApi.toggleBloomScore(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-comments"] });
    },
  });
};

export const useCommentBloomScore = (commentId: string) => {
  return useQuery({
    queryKey: ["comment-bloom-score", commentId],
    queryFn: () => campaignsApi.getCommentBloomScore(commentId),
    enabled: !!commentId,
  });
};

// Auth hooks
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authApi.login(credentials),
    onSuccess: (response) => {
      // Set user data in cache
      queryClient.setQueryData(queryKeys.user, response.user);

      toast({
        title: "Welcome back! 👋",
        description: `You've successfully logged in as ${response.user.role.toLowerCase()}.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Login failed",
        description:
          error instanceof Error ? error.message : "Invalid credentials",
        variant: "destructive",
      });
    },
  });
};

export const useRegister = () => {
  return useMutation({
    mutationFn: (userData: RegisterRequest) => authApi.register(userData),
    onSuccess: () => {
      toast({
        title: "Registration successful! 🎉",
        description: "Welcome to FundOrFling!",
      });
    },
    onError: (error) => {
      toast({
        title: "Registration failed",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return () => {
    authApi.logout();
    queryClient.clear(); // Clear all cached data

    toast({
      title: "Logged out successfully",
      description: "You have been logged out of your account.",
    });
  };
};

// Waitlist hooks
export const useWaitlistCount = (campaignId: string) => {
  return useQuery({
    queryKey: ["waitlist-count", campaignId],
    queryFn: () => campaignsApi.getWaitlistCount(campaignId),
    enabled: !!campaignId,
  });
};

export const useWaitlistSubscriptions = (campaignId: string) => {
  return useQuery({
    queryKey: ["waitlist-subscriptions", campaignId],
    queryFn: () => campaignsApi.getWaitlistSubscriptions(campaignId),
    enabled: !!campaignId,
  });
};

export const useSubscribeToWaitlist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      data,
    }: {
      campaignId: string;
      data: CreateWaitlistSubscriptionRequest;
    }) => campaignsApi.subscribeToWaitlist(campaignId, data),
    onSuccess: (_, { campaignId }) => {
      // Invalidate waitlist count and subscriptions
      queryClient.invalidateQueries({
        queryKey: ["waitlist-count", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["waitlist-subscriptions", campaignId],
      });

      toast({
        title: "Successfully subscribed! 🎉",
        description: "You'll be notified about updates to this campaign.",
      });
    },
    onError: (error) => {
      toast({
        title: "Subscription failed",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    },
  });
};

export const useUnsubscribeFromWaitlist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      email,
    }: {
      campaignId: string;
      email: string;
    }) => campaignsApi.unsubscribeFromWaitlist(campaignId, email),
    onSuccess: (_, { campaignId }) => {
      // Invalidate waitlist data
      queryClient.invalidateQueries({
        queryKey: ["waitlist-count", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["waitlist-subscriptions", campaignId],
      });

      toast({
        title: "Unsubscribed successfully",
        description: "You have been removed from the waitlist.",
      });
    },
    onError: (error) => {
      toast({
        title: "Unsubscribe failed",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    },
  });
};

// Pricing Feedback hooks
export const usePricingFeedback = (campaignId: string) => {
  return useQuery({
    queryKey: ["pricingFeedback", campaignId],
    queryFn: () => campaignsApi.getPricingFeedback(campaignId),
    enabled: !!campaignId,
  });
};

export const useCreatePricingFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      data,
    }: {
      campaignId: string;
      data: CreatePricingFeedbackRequest;
    }) => campaignsApi.createPricingFeedback(campaignId, data),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({
        queryKey: ["pricingFeedback", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["pricingFeedbackAnalytics", campaignId],
      });
    },
  });
};

// Pricing Feedback Analytics hook
export const usePricingFeedbackAnalytics = (campaignId: string) => {
  return useQuery({
    queryKey: ["pricingFeedbackAnalytics", campaignId],
    queryFn: () => campaignsApi.getPricingFeedbackAnalytics(campaignId),
    enabled: !!campaignId,
  });
};

// Response hooks
export const useCampaignResponses = (campaignId: string) => {
  return useQuery({
    queryKey: ["campaignResponses", campaignId],
    queryFn: () => campaignsApi.getCampaignResponses(campaignId),
    enabled: !!campaignId,
  });
};

export const useUserResponsesForCampaign = (campaignId: string) => {
  return useQuery({
    queryKey: ["userResponses", campaignId],
    queryFn: () => campaignsApi.getUserResponsesForCampaign(campaignId),
    enabled: !!campaignId,
  });
};

export const useHasUserRespondedToCampaign = (campaignId: string) => {
  return useQuery({
    queryKey: ["hasUserResponded", campaignId],
    queryFn: () => campaignsApi.hasUserRespondedToCampaign(campaignId),
    enabled: !!campaignId,
  });
};

export const useCreateResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      data,
    }: {
      campaignId: string;
      data: CreateResponseRequest;
    }) => campaignsApi.createResponse(campaignId, data),
    onSuccess: (_, { campaignId }) => {
      // Invalidate campaign data to refresh response count
      queryClient.invalidateQueries({
        queryKey: ["campaign", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaignResponses", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["userResponses", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["hasUserResponded", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaigns"],
      });
    },
  });
};

export const useCreateMultipleResponses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      responses,
    }: {
      campaignId: string;
      responses: CreateResponseRequest[];
    }) => campaignsApi.createMultipleResponses(campaignId, responses),
    onSuccess: (_, { campaignId }) => {
      // Invalidate campaign data to refresh response count
      queryClient.invalidateQueries({
        queryKey: ["campaign", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaignResponses", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["userResponses", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["hasUserResponded", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaigns"],
      });
    },
  });
};

// User profile hooks
export const useUserProfile = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: queryKeys.userProfile,
    queryFn: userApi.getProfile,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: isAuthenticated,
  });
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => userApi.updateProfile(data),
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(queryKeys.userProfile, updatedProfile);
      toast({
        title: "Profile updated successfully! ✨",
        description: "Your profile information has been saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to update profile",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    },
  });
};

// User settings hooks
export const useUserSettings = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: queryKeys.userSettings,
    queryFn: userApi.getSettings,
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: isAuthenticated,
  });
};

export const useUpdateUserSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateSettingsRequest) => userApi.updateSettings(data),
    onSuccess: () => {
      // Invalidate user settings query
      queryClient.invalidateQueries({ queryKey: queryKeys.userSettings });

      toast({
        title: "Settings updated successfully! ✨",
        description: "Your notification preferences have been saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to update settings",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => userApi.changePassword(data),
    onSuccess: () => {
      toast({
        title: "Password changed successfully! 🔐",
        description: "Your password has been updated.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to change password",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    },
  });
};

// User notifications hooks
export const useUserNotifications = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: queryKeys.userNotifications,
    queryFn: userApi.getNotifications,
    staleTime: 1 * 60 * 1000, // 1 minute
    enabled: isAuthenticated,
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userApi.markNotificationAsRead(id),
    onSuccess: (_, notificationId) => {
      // Update the specific notification in cache
      queryClient.setQueryData(
        queryKeys.userNotifications,
        (oldData: Notification[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((notification: Notification) =>
            notification.id === notificationId
              ? { ...notification, read: true }
              : notification
          );
        }
      );
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => userApi.markAllNotificationsAsRead(),
    onSuccess: () => {
      // Mark all notifications as read in cache
      queryClient.setQueryData(
        queryKeys.userNotifications,
        (oldData: Notification[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((notification: Notification) => ({
            ...notification,
            read: true,
          }));
        }
      );
      toast({
        title: "All notifications marked as read",
        description: "You're all caught up!",
      });
    },
  });
};

// User campaigns hooks
export const useUserCampaigns = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: queryKeys.myCampaigns,
    queryFn: userApi.getUserCampaigns,
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: isAuthenticated,
  });
};

// User activity hooks
export const useUserActivity = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: queryKeys.userActivity,
    queryFn: () => activityApi.getUserActivities(),
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Admin activity hooks
export const useAdminActivities = (filters?: ActivityFilters) => {
  const { isAuthenticated, user } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.adminActivities, filters],
    queryFn: () => activityApi.getAdminActivities(filters),
    enabled: isAuthenticated && user?.role === "ADMIN",
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useRecentActivities = (limit?: number) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.recentActivities, limit],
    queryFn: () => activityApi.getRecentActivities(limit),
    enabled: isAuthenticated,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useActivityStats = () => {
  const { isAuthenticated, user } = useAuth();

  return useQuery({
    queryKey: queryKeys.activityStats,
    queryFn: () => activityApi.getActivityStats(),
    enabled: isAuthenticated && user?.role === "ADMIN",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Analytics hooks
export const useDashboardOverview = () => {
  const { isAuthenticated, user } = useAuth();

  console.log(
    "🔍 [Frontend] useDashboardOverview called - isAuthenticated:",
    isAuthenticated,
    "user role:",
    user?.role
  );

  return useQuery({
    queryKey: queryKeys.dashboardOverview,
    queryFn: async () => {
      console.log("🔍 [Frontend] Fetching dashboard overview...");
      const data = await analyticsApi.getDashboardOverview();
      console.log("✅ [Frontend] Dashboard overview data received:", data);
      return data;
    },
    enabled: isAuthenticated && user?.role === "ADMIN",
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useUserAnalytics = () => {
  const { isAuthenticated, user } = useAuth();

  console.log(
    "🔍 [Frontend] useUserAnalytics called - isAuthenticated:",
    isAuthenticated,
    "user role:",
    user?.role
  );

  return useQuery({
    queryKey: queryKeys.userAnalytics,
    queryFn: async () => {
      console.log("🔍 [Frontend] Fetching user analytics...");
      const data = await analyticsApi.getUserAnalytics();
      console.log("✅ [Frontend] User analytics data received:", data);
      return data;
    },
    enabled: isAuthenticated && user?.role === "ADMIN",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCampaignAnalytics = () => {
  const { isAuthenticated, user } = useAuth();

  console.log(
    "🔍 [Frontend] useCampaignAnalytics called - isAuthenticated:",
    isAuthenticated,
    "user role:",
    user?.role
  );

  return useQuery({
    queryKey: queryKeys.campaignAnalytics,
    queryFn: async () => {
      console.log("🔍 [Frontend] Fetching campaign analytics...");
      const data = await analyticsApi.getCampaignAnalytics();
      console.log("✅ [Frontend] Campaign analytics data received:", data);
      return data;
    },
    enabled: isAuthenticated && user?.role === "ADMIN",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useEngagementAnalytics = () => {
  const { isAuthenticated, user } = useAuth();

  console.log(
    "🔍 [Frontend] useEngagementAnalytics called - isAuthenticated:",
    isAuthenticated,
    "user role:",
    user?.role
  );

  return useQuery({
    queryKey: queryKeys.engagementAnalytics,
    queryFn: async () => {
      console.log("🔍 [Frontend] Fetching engagement analytics...");
      const data = await analyticsApi.getEngagementAnalytics();
      console.log("✅ [Frontend] Engagement analytics data received:", data);
      return data;
    },
    enabled: isAuthenticated && user?.role === "ADMIN",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useConversionFunnel = () => {
  const { isAuthenticated, user } = useAuth();

  console.log(
    "🔍 [Frontend] useConversionFunnel called - isAuthenticated:",
    isAuthenticated,
    "user role:",
    user?.role
  );

  return useQuery({
    queryKey: queryKeys.conversionFunnel,
    queryFn: async () => {
      console.log("🔍 [Frontend] Fetching conversion funnel...");
      const data = await analyticsApi.getConversionFunnel();
      console.log("✅ [Frontend] Conversion funnel data received:", data);
      return data;
    },
    enabled: isAuthenticated && user?.role === "ADMIN",
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUserContributions = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: queryKeys.userContributions,
    queryFn: () => userApi.getUserContributions(),
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Debug hook for user count
export const useUserCount = () => {
  const { isAuthenticated, user } = useAuth();

  return useQuery({
    queryKey: ["analytics", "user-count"],
    queryFn: () => analyticsApi.getUserCount(),
    enabled: isAuthenticated && user?.role === "ADMIN",
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Debug hook for growth data
export const useGrowthDataDebug = () => {
  const { isAuthenticated, user } = useAuth();

  return useQuery({
    queryKey: ["analytics", "growth-data-debug"],
    queryFn: () => analyticsApi.getGrowthDataDebug(),
    enabled: isAuthenticated && user?.role === "ADMIN",
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};
